import { normalizeFootballGameData } from '@sportal365/smp-widget-normalizers';
import {
  defaultHandleEventClick,
  defaultHandleTeamClick,
  defaultHandleTournamentClick,
  extractOnlyDateSDKOptions,
  getLabelValue,
} from '@sportal365/smp-widget-utils';
import {
  GameTeam,
  ConfigContext,
  Round,
  SMPContainer,
  SMPFlex,
  SMPSportEntity,
  SMPText,
  EventInfo as SingleEventInfo,
  StakesDisplayCustom,
  StakesDisplayFixed,
  Tournament,
} from '@velinsi/smp-widgets-ui';
import { FC, useState } from 'react';
import { GenericSize, MAX_MOBILE_WIDTH } from '../../../../../packages/constants/generic';
import { Sports } from '../../../../../packages/constants/sportType';
import { BASE_VARIANT, LIVE_VARIANT } from '../../../../../packages/constants/statusTextVariants';
import { IN_PROGRESS, NOT_STARTED } from '../../../../../packages/constants/statusTypes';
import useResizeObserver from '../../../../../packages/hooks/useResizeObserver';
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';
import { BaseWidgetProps, IBaseTeam, IStakesConfig, ITournament, OddsMarketTypes } from '../../../../../packages/types';
import MatchContainer from '../../components/MatchContainer';
import useSingleEvent from '../../hooks/useSingleEvent';

export type StakesWidgetProps = BaseWidgetProps<IStakesConfig>;

export const StakesWidget: FC<StakesWidgetProps> = ({ themes, config, subscribers, shadow, getAuthToken, signOut, sdkOptions, cuid }) => {
  const [htmlElement, setHtmlElement] = useState<HTMLElement>();
  const [size, setSize] = useState<GenericSize>();
  const measuredRef = useResizeObserver((element) => {
    if (!htmlElement || element !== htmlElement) {
      setHtmlElement(element);
    }

    setSize(element.offsetWidth >= MAX_MOBILE_WIDTH ? GenericSize.DEFAULT : GenericSize.SMALL);
  });

  const {
    showWidget,
    handleClick,
    widgetData: { footballSingleEvent, odds, isLoading },
  } = useSingleEvent({
    subscribers,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    dataRefreshTime: config?.dataRefreshTime,
    dataMatchId: config?.dataMatchId,
    dataOddsDisplay: true,
    dataOddsBettingId: config?.dataOddsBettingId,
    dataOddsPreEventOnly: config?.dataOddsPreEventOnly,
    dataOddsMarketValueType: 'decimal',
    dataOddsMarket: OddsMarketTypes['ONEXTWO'],
    dataWidgetCuid: cuid,
  });

  if (!showWidget) return null;

  const match = normalizeFootballGameData({
    game: footballSingleEvent,
    dataEntityLinks: config?.dataEntityLinks,
    sdkDateOptions: extractOnlyDateSDKOptions(sdkOptions),
  });

  const handleTeamClick = ({ id, name }: IBaseTeam) => defaultHandleTeamClick(handleClick, { id, name });

  const handleEventClick = (event: string | number) => defaultHandleEventClick(handleClick, Number(event));

  const handleTournamentClick = (tournament: ITournament) => defaultHandleTournamentClick(handleClick, tournament.id, tournament.name);

  if (!match || !match.homeTeam || !match.awayTeam || !odds || odds.length === 0) {
    return null;
  }

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <ThemeProvider isLoading={isLoading} shadow={shadow} themes={themes} dataTheme={config?.dataTheme} css={{ bc: '$stakesBgColor' }}>
        <SMPContainer
          ref={measuredRef}
          size='4'
          variant='footballSingleEventContainer'
          css={{ pb: size !== GenericSize.SMALL ? '$2_5' : '$5', bc: 'inherit' }}
        >
          <MatchContainer variant={footballSingleEvent?.status?.type === IN_PROGRESS ? LIVE_VARIANT : BASE_VARIANT} type='singleEvent'>
            <SMPFlex css={{ mb: '$1', pl: '$3' }}>
              {match.tournament && (
                <Tournament
                  sport={Sports.FOOTBALL}
                  tournament={match.tournament}
                  handleTournamentClick={handleTournamentClick}
                  hideLogo
                  hideStandingsIcon
                  hideSubName
                  variant='footballSingleEventTournamentContainer'
                />
              )}
            </SMPFlex>
            <SMPFlex css={{ pl: '$3', pb: match.round ? '' : '$3' }}>
              {match.round && (
                <Round
                  round={match?.round}
                  matchDate={match.kickOffTime}
                  dataRoundLabel={config?.dataLabels?.round}
                  sdkOptions={extractOnlyDateSDKOptions(sdkOptions)}
                />
              )}
            </SMPFlex>
            <SMPFlex
              align='center'
              gap='none'
              variant='eventInfo'
              css={{
                mx: 'auto',
                width: '100%',
                maxWidth: '400px',
                pl: '$3',
                [`& ${SMPText}:not(.vs)`]: {
                  fontSize: '$4',
                  fontWeight: '700',
                },
              }}
            >
              <GameTeam
                isMobile
                sportEntityVariant={'homeTeam'}
                handleTeamClick={handleTeamClick}
                team={match?.homeTeam}
                teamLink={match?.homeTeam.url}
                teamName={match?.homeTeam?.name}
                teamLogo={match?.homeTeam.logo}
                urlTarget={match?.urlTarget}
                imageVariant='stakesTeamLogo'
              />

              {match.matchStatus?.type === NOT_STARTED ? (
                <SMPSportEntity
                  href={match.matchLink}
                  target={match.urlTarget}
                  onClick={() => handleEventClick && handleEventClick(match.id)}
                  style={{ flexGrow: 'unset', justifyContent: 'center' }}
                >
                  <SMPText className='vs' size='6' variant='contrast' weight='700'>
                    {getLabelValue(config?.dataLabels?.vs, 'VS')}
                  </SMPText>
                </SMPSportEntity>
              ) : (
                <SingleEventInfo handleEventClick={handleEventClick} match={match} matchLink={match.matchLink} />
              )}

              <GameTeam
                isMobile
                sportEntityVariant={'awayTeam'}
                handleTeamClick={handleTeamClick}
                team={match?.awayTeam}
                teamLink={match?.awayTeam.url}
                teamName={match?.awayTeam?.name}
                teamLogo={match?.awayTeam.logo}
                urlTarget={match?.urlTarget}
                imageVariant='stakesTeamLogo'
              />
            </SMPFlex>
          </MatchContainer>
        </SMPContainer>

        {config?.dataFixed !== false ? (
          <StakesDisplayFixed
            amount={config?.dataAmount}
            handleClick={handleClick}
            htmlElement={htmlElement}
            odds={odds}
            size={size}
            teams={[match.homeTeam, match.awayTeam]}
            theme={config?.dataTheme}
            currencyPosition={config?.dataCurrencySymbolPosition}
          />
        ) : (
          <StakesDisplayCustom
            initialAmount={config?.dataAmount}
            handleClick={handleClick}
            odds={odds}
            size={size}
            theme={config?.dataTheme}
            currencyPosition={config?.dataCurrencySymbolPosition}
          />
        )}
      </ThemeProvider>
    </ConfigContext.Provider>
  );
};
